import { shopifyStudyOverviewPresentationSchema } from './shopify-study-results-overview.js';
import { resultPhraseProposalSchema } from './study-next-test-selection.js';
export { shopifyStudyOverviewPresentationSchema } from './shopify-study-results-overview.js';
import { resultCodebookSchema } from './response-codebook.js';
import { resultTreatmentCardSchema } from './shopify-study-results-card.js';
export { resultTreatmentCardSchema, resultRespondentGridSchema, resultTestedContentSchema, } from './shopify-study-results-card.js';
/** Server-owned direct-results display. Values are validated at the read boundary; React supplies layout and interaction only. */
import * as z from 'zod';
import { resultQuoteSchema } from './shopify-study-results-source.js';
export { resultQuoteSchema } from './shopify-study-results-source.js';
import { builderFieldMessageSchema, builderProductBlockSchema } from './shopify-study-builder-blocks.js';
/** Full saved evidence can repeat an answer under several factors; keep Admin reads bounded without truncating it. */
export const STUDY_RESULTS_RESPONSE_MAX_BYTES = 8_000_000;
const text = z.string();
const nullableText = text.nullable();
const tone = z.enum(['info', 'success', 'warning', 'critical']);
/** Results notices are native callouts, not field-validation lines. Authored message text is preserved. */
export const resultMessageSchema = builderFieldMessageSchema.extend({
    severity: z.enum(['error', 'warning', 'info']),
    heading: text.optional(),
});
export const resultHeaderSchema = z.strictObject({
    title: text,
    description: text,
    badge: z.strictObject({ text, tone }).nullable(),
    help: z.strictObject({ heading: text, why: text, how: text }).nullable(),
});
export const resultActionSchema = z.strictObject({
    id: z.enum(['back', 'edit', 'delete', 'retry_read']),
    label: text,
    href: nullableText,
});
const audienceRow = z.strictObject({
    armId: text,
    armLabel: text,
    imageUrl: nullableText,
    description: nullableText,
    value: text,
    progress: z.number().min(0).max(100),
    accessibilityLabel: text,
});
export const resultAudienceSchema = z.strictObject({
    notes: z.array(text),
    countLabel: text,
    heading: text,
    rowLabel: text,
    metricLabel: text,
    groups: z.array(z.strictObject({
        id: text,
        title: text,
        dimension: text,
        rows: z.array(audienceRow),
    })),
});
const evidenceGroup = z.strictObject({
    title: text,
    emptyText: text,
    answers: z.array(resultQuoteSchema),
});
const phraseObservation = z.strictObject({
    id: text,
    title: text,
    interpretation: text,
    answer: resultQuoteSchema,
    evidence: z.array(evidenceGroup),
    passages: z
        .array(z.strictObject({
        role: z.enum(['supporting', 'qualifying']),
        title: text,
        explanation: text,
        answer: resultQuoteSchema.options[0],
        counts: z.array(z.strictObject({ label: text, detail: text })),
    }))
        .optional(),
});
export const resultPhrasesSchema = z.strictObject({
    codebook: resultCodebookSchema.optional(),
    navigation: z
        .strictObject({
        pageLabel: text,
        previous: z.strictObject({ label: text, href: text }).nullable(),
        next: z.strictObject({ label: text, href: text }).nullable(),
    })
        .optional(),
    notes: z.array(text),
    scopeHeading: text,
    scope: z.array(z.strictObject({ label: text, detail: text })),
    observations: z.array(phraseObservation),
    actionsHeading: text,
    actions: z.array(z.strictObject({
        id: text,
        title: text,
        suggestion: text,
        reason: text,
        notice: text,
        evidence: z.array(evidenceGroup),
    })),
    questionsHeading: text,
    questions: z.array(z.strictObject({ id: text, question: text, detail: text })),
    proposalGroups: z.array(z.strictObject({ title: text, proposals: z.array(resultPhraseProposalSchema) })),
    otherAnswers: z.array(resultQuoteSchema),
    labels: z.strictObject({ evidence: text, explore: text, otherAnswers: text }),
});
const classifiedAnswer = z.strictObject({
    id: text,
    text,
    source: text,
    armId: text,
    notes: z.array(text),
});
export const resultClassificationsSchema = z.strictObject({
    notes: z.array(text),
    objective: text,
    decision: nullableText,
    sample: z.strictObject({ title: text, notes: z.array(text) }).nullable(),
    groups: z.array(z.strictObject({
        id: text,
        title: text,
        role: text,
        answers: z.array(classifiedAnswer),
    })),
    reviewHeading: text,
    review: z.array(classifiedAnswer),
});
const pageSelection = z
    .string()
    .regex(/^[1-9]\d{0,5}$/)
    .optional();
/** Read-only browse facts; every link target and selection is supplied by the server. */
export const resultQuestionSelectionSchema = z.strictObject({
    phrasesPage: pageSelection,
    nextTestsPage: pageSelection,
    questionId: z.string().min(1).optional(),
    answerArmId: z.string().min(1).optional(),
    answerPage: pageSelection,
});
const questionChoice = z.strictObject({
    id: text,
    label: text,
    selected: z.boolean(),
    href: text,
});
const pageLink = z.strictObject({ label: text, href: text });
export const resultQuestionNavigationSchema = z.strictObject({
    questionLabel: text,
    versionLabel: text,
    questions: z.array(questionChoice).min(1),
    versions: z.array(questionChoice).min(1),
    pageLabel: text,
    previous: pageLink.nullable(),
    next: pageLink.nullable(),
});
export const resultQuestionsSchema = z.strictObject({
    navigation: resultQuestionNavigationSchema,
    notes: z.array(text),
    groups: z.array(z.strictObject({
        id: text,
        title: text,
        notice: nullableText,
        count: text,
        answers: z.array(z.strictObject({
            id: text,
            respondent: text,
            armLabel: text,
            imageUrl: nullableText,
            description: nullableText,
            text,
            assessment: nullableText,
        })),
    })),
});
export const resultInquirySchema = z.strictObject({
    notes: z.array(text),
    conversations: z
        .array(z.strictObject({
        id: text,
        source: text,
        statusLabel: text,
        writerStatusLabel: nullableText,
        originalLabel: text,
        originalAnswers: z.array(z.strictObject({ id: text, question: text, response: text })),
        followup: z
            .strictObject({
            questionLabel: text,
            question: text,
            answerLabel: text,
            answer: nullableText,
        })
            .nullable(),
    }))
        .optional(),
    proposals: z
        .array(z.strictObject({
        id: text,
        statusLabel: text,
        missingLabel: text,
        missingDistinction: text,
        questionLabel: text,
        question: text,
        evidence: resultQuoteSchema,
    }))
        .optional(),
    runs: z.array(z.strictObject({
        id: text,
        title: text,
        message: text,
        exchanges: z.array(z.strictObject({
            id: text,
            question: text,
            originalLabel: text,
            originalAnswer: text,
            followups: z.array(z.strictObject({
                questionLabel: text,
                question: text,
                answerLabel: text,
                answer: text,
                warning: nullableText,
            })),
            findings: z.array(z.strictObject({ id: text, title: text, description: text })),
        })),
    })),
});
const section = {
    id: text,
    header: resultHeaderSchema,
    messages: z.array(builderFieldMessageSchema),
    disclosure: z.boolean(),
};
export const resultSectionSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        ...section,
        kind: z.literal('overview'),
        data: shopifyStudyOverviewPresentationSchema,
    }),
    z.strictObject({
        ...section,
        kind: z.literal('versions'),
        data: z.strictObject({
            cards: z.array(resultTreatmentCardSchema),
            layout: z.enum(['columns', 'rows']),
            footer: nullableText,
        }),
    }),
    z.strictObject({
        ...section,
        kind: z.literal('audience'),
        data: resultAudienceSchema,
    }),
    z.strictObject({
        ...section,
        kind: z.literal('phrases'),
        data: resultPhrasesSchema,
    }),
    z.strictObject({
        ...section,
        kind: z.literal('analysis_status'),
        data: z.strictObject({
            state: z.enum(['queued', 'running', 'failed', 'uncertain', 'empty', 'unavailable']),
            message: text,
            loading: z.boolean(),
        }),
    }),
    z.strictObject({
        ...section,
        kind: z.literal('evidence_entry'),
        data: z.strictObject({
            notes: z.array(text),
            items: z.array(z.strictObject({ id: text, title: text })).min(1),
            action: z.strictObject({ tabId: text, label: text }),
        }),
    }),
    z.strictObject({
        ...section,
        kind: z.literal('classifications'),
        data: resultClassificationsSchema,
    }),
    z.strictObject({
        ...section,
        kind: z.literal('questions'),
        data: resultQuestionsSchema,
    }),
    z.strictObject({
        ...section,
        kind: z.literal('inquiry'),
        data: resultInquirySchema,
    }),
]);
const tab = z.strictObject({
    id: text,
    label: text,
    sections: z.array(resultSectionSchema).min(1),
});
export const resultProductDisplaySchema = z.strictObject({
    header: resultHeaderSchema,
    data: builderProductBlockSchema,
});
const common = {
    product: resultProductDisplaySchema,
    heading: text,
    messages: z.array(resultMessageSchema),
    actions: z.array(resultActionSchema),
    pastRuns: z.array(z.strictObject({ id: text, requestedAt: text, status: text, href: text })),
    refreshAfterMs: z.literal(5000).nullable(),
};
const statusData = z.strictObject({
    title: text,
    badge: z.strictObject({ text, tone }).nullable(),
    startedAtLabel: text,
    supportLabel: text,
    lines: z.array(text),
    progress: z.number().min(0).max(100).nullable(),
    startedAt: nullableText,
    supportUrl: nullableText,
    versions: z.array(resultTreatmentCardSchema),
});
export const shopifyStudyResultsViewSchema = z
    .discriminatedUnion('state', [
    z.strictObject({
        ...common,
        state: z.literal('report'),
        runId: text,
        defaultTabId: text,
        tabs: z.array(tab).min(1),
    }),
    ...['not_run', 'running', 'failed', 'cancelled', 'report_unavailable', 'status_unavailable'].map((state) => z.strictObject({ ...common, state: z.literal(state), data: statusData })),
])
    .superRefine((view, ctx) => {
    if (view.state !== 'report')
        return;
    const ids = view.tabs.map((item) => item.id);
    if (new Set(ids).size !== ids.length || !ids.includes(view.defaultTabId))
        ctx.addIssue({
            code: 'custom',
            message: 'Result tabs must be unique and include the selected default.',
        });
    for (const tab of view.tabs) {
        for (const section of tab.sections) {
            if (section.kind === 'evidence_entry' && !ids.includes(section.data.action.tabId))
                ctx.addIssue({
                    code: 'custom',
                    message: 'Evidence actions must reference a supplied results tab.',
                });
            if (section.kind !== 'versions')
                continue;
            for (const card of section.data.cards) {
                if (card.reactionAction && !ids.includes(card.reactionAction.tabId))
                    ctx.addIssue({
                        code: 'custom',
                        message: 'Card reaction actions must reference a supplied results tab.',
                    });
            }
        }
    }
    const sections = view.tabs.flatMap((item) => item.sections.map((entry) => entry.id));
    if (new Set(sections).size !== sections.length)
        ctx.addIssue({
            code: 'custom',
            message: 'Result section IDs must be unique.',
        });
});
//# sourceMappingURL=shopify-study-results-view.js.map